import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Grid, Row, Col } from '@zendeskgarden/react-grid';
import Alert from 'theme/Alert';
import { forEach, get, reject } from 'lodash';
import { Redirect } from 'react-router';
import ListingsHero from 'routes/Products/components/ListingsHero/ListingsHero';
import ProductsStatusMessage from 'routes/Products/components/ProductsStatusMessage/ProductsStatusMessage';
import {
  ProductsSection,
  ProductsSectionInner,
  ProductsPage,
  ProductsSectionHeader,
} from 'routes/Products/Products.styles';
import { useBranch } from 'baobab-react/hooks';
import { getListingDetails, getSimilarListings } from 'state/listings/actions';
import { addProductToWishlist } from 'state/products/actions';
import { HelmetWrapper } from 'utility/DefaultMeta';
import Spinner from 'components/Spinner/Spinner';
import { variables } from 'theme/variables';
import PageViewWrapper from 'higherOrder/PageViewWrapper';
import { listingShape } from 'propTypesObjects';
import ProductsPipeFacts from 'routes/Products/components/ProductsPipeFacts/ProductsPipeFacts';
import { webRefactorSwitch } from '../../../../utility/environment';
import RelatedListingsMiniTable from '../../components/RelatedListings/RelatedListingsMiniTable';

const { spacing_md: spacingMd } = variables;

function getMeta(productData) {
  if (!productData) {
    return null;
  }
  const image = get(productData, 'images[0].image', '');
  const title = `Listing: ${productData.name}`;
  const {
    price,
    condition_name: condition,
    quantity,
    status,
    description,
  } = productData;
  const brand = get(productData, 'manufacturer.brand');
  const meta = [];
  const properties = {
    title,
    description,
    image,
  };

  forEach(properties, (v, k) => {
    if (v) {
      meta.push({ property: `og:${k}`, content: v });
      meta.push({ name: `twitter:${k}`, content: v });
    }
  });
  meta.push({ name: 'description', content: description });
  meta.push({ name: 'robots', content: 'all' });

  const json = {
    '@context': 'http://schema.org/',
    '@type': 'product',
    name: title,
    description,
    brand: {
      name: brand,
    },
    offers: {
      '@type': 'Offer',
      price,
      availability:
        status === 'active'
          ? 'http://schema.org/InStock'
          : 'http://schema.org/OutOfStock',
      priceCurrency: 'USD',
      inventoryLevel: quantity,
      itemCondition: condition,
    },
  };

  const script = [
    { type: 'application/ld+json', innerHTML: JSON.stringify(json, null, 2) },
  ];

  const link = [];
  return { title, meta, link, script };
}

function ListingV2({ listingId, routeSlug, location, productDetails }) {
  const { user, similarListings } = useBranch({
    productDetails: ['listings', 'listingDetails', listingId],
    user: ['authentication', 'sessions'],
    similarListings: ['listing', 'similarListing', listingId || ''],
  });

  const metadata = getMeta(productDetails.result);
  const listingResultId = get(productDetails.result, 'id');
  const productCriteria = get(productDetails, 'result.criteria');
  const otherListings = get(similarListings, 'result.docs');
  const productSlug = get(productDetails, 'result.slug');
  const slugsMatch = !(productSlug && routeSlug !== productSlug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [listingId]);

  useEffect(() => {
    getListingDetails(listingId);
  }, [listingId]);

  useEffect(() => {
    if (productCriteria) {
      getSimilarListings({
        criteria: { ...productCriteria, status: ['active'] },
        id: listingId,
      });
    }
  }, [listingId, listingResultId, productCriteria]);

  const onAddToWishlist = useCallback(async () => {
    if (get(user, 'user_id')) {
      return addProductToWishlist({
        listingId,
        user,
        product: productDetails.result,
      });
    }
    return Promise.resolve();
  }, [listingId, productDetails.result, user]);

  const status = get(productDetails, 'result.status');
  const isDeleted = status === 'deleted';

  return (
    <ProductsPage>
      {!slugsMatch && !get(location, 'state.fromInternal') ? (
        <Redirect
          from={`/l/${routeSlug}/${listingId}`}
          to={`/l/${productSlug}/${listingId}`}
        />
      ) : null}
      {productDetails.result ? <HelmetWrapper metadata={metadata} /> : null}

      {productDetails.loading && !get(productDetails, 'result') ? (
        <Spinner style={{ marginTop: '80px' }} size="15px" />
      ) : null}

      {productDetails.error ? (
        <ProductsSection>
          <ProductsSectionInner first>
            <Alert
              style={{ marginBottom: spacingMd }}
              small
              title={productDetails.error}
              type="error"
            />
          </ProductsSectionInner>
        </ProductsSection>
      ) : null}
      {isDeleted ? (
        <ProductsSection>
          <ProductsSectionInner>
            <ProductsStatusMessage status={status} />
          </ProductsSectionInner>
        </ProductsSection>
      ) : null}
      {productDetails.result && !isDeleted ? (
        <div>
          <ProductsSection>
            <ProductsSectionInner>
              <ListingsHero
                userId={get(user, 'user_id')}
                productDetails={productDetails.result}
                onAddToWishlist={onAddToWishlist}
              />
            </ProductsSectionInner>
          </ProductsSection>
          <ProductsSection>
            <ProductsSectionInner webRefactor={webRefactorSwitch}>
              <ProductsPipeFacts productDetails={productDetails.result} />
            </ProductsSectionInner>
          </ProductsSection>
          {get(otherListings, 'length') ? (
            <ProductsSection style={{ marginBottom: 0 }}>
              <ProductsSectionInner>
                <Grid>
                  <Row>
                    <Col>
                      <ProductsSectionHeader
                        tag="h2"
                        style={{ fontSize: '26px', marginBottom: '20px' }}
                        paddingBottomMd
                        scaled
                        bold
                      >
                        {`Recommended Listings similar to ${
                          productDetails?.result?.title ||
                          productDetails?.result?.name
                        }`}
                      </ProductsSectionHeader>
                    </Col>
                  </Row>
                </Grid>
                <RelatedListingsMiniTable
                  data={reject(otherListings, { id: Number(listingId) })}
                />
              </ProductsSectionInner>
            </ProductsSection>
          ) : null}
        </div>
      ) : null}
    </ProductsPage>
  );
}

ListingV2.propTypes = {
  listingId: PropTypes.string,
  routeSlug: PropTypes.string,
  // LINT OVERRIDE #2
  // https://github.com/remix-run/react-router/issues/6177
  // External object
  // eslint-disable-next-line react/forbid-prop-types
  location: PropTypes.object,
  productDetails: listingShape,
};

export default PageViewWrapper(ListingV2, { pageName: 'ListingV2' });
